/**
 * OutlineCreases.ts
 * -----------------------------------------------------------------------------
 * THE OTHER HALF OF A LINE DRAWING: the creases, not just the silhouette.
 *
 * SelectionHull draws the outer contour and only the outer contour — that is
 * what an inverted hull IS, a slightly larger copy whose back faces peek out
 * round the edge of the real one. It is the right tool for saying "this is a
 * separate component", which is what it was written for.
 *
 * It cannot draw the edge where two faces of the SAME component meet. On a
 * column that hardly matters; on a girder it is most of the drawing. A precast
 * segment is a box with a haunch, and a box seen from a corner is three visible
 * faces meeting along two arrises — the hull gives you the outside of the box
 * and nothing else, so a run of girder comes out as a long flat lozenge with a
 * line round it. What makes it read as a structure is the arris down the
 * soffit/web junction.
 *
 * `EdgesGeometry` is three's own answer and it is right here: it walks the
 * triangles, keeps every edge where the angle between the two faces sharing it
 * is over a threshold, and throws the rest away. It hashes vertex POSITIONS
 * (4 decimal places) rather than requiring an index, which matters because the
 * exporter emits non-indexed soup — three vertices per triangle, never shared —
 * so that nothing upstream has to change.
 *
 * THE THRESHOLD IS THE WHOLE THING, and the instinct is backwards: setting it
 * high to keep the drawing clean is what removes most of the drawing. A chamfer
 * or an eased arris means the two faces meeting along that corner are obtuse
 * rather than square, so the angle between their NORMALS is well under 90 — a
 * threshold picked to "only keep real corners" drops every one of them and
 * leaves the flat lozenge again. {@link DEFAULT_ANGLE} is set low enough to keep
 * chamfers and still drop what should be dropped: a 16-sided duct's facets are
 * 22 degrees apart, so a tendon tube stays smooth.
 *
 * ONE DRAW CALL FOR THE WHOLE MODEL, which is where this differs from the same
 * idea applied to a scene of separate objects. There, each mesh's creases have
 * to be a child of that mesh so they inherit its transform. Here every part's
 * positions are ALREADY world-space (BridgeScene uploads exporter coordinates
 * verbatim and leaves the meshes at the origin), so every part's creases can be
 * concatenated into a single LineSegments — the same trick, and the same
 * reasoning, as BridgeScene.buildMergedLines does for the engine's own edges.
 *
 * AND IT IS IMMUNE TO TRANSPARENCY, unlike the hull. A hull behind a
 * see-through surface shows its whole back-face shell and reads as a filled
 * shape; a line has no interior to show through, so creases over a ghosted deck
 * stay a drawing.
 */

import * as THREE from 'three';

/**
 * Default crease threshold in degrees — the angle between the normals of the two
 * faces sharing an edge, above which the edge is kept.
 *
 * 30 rather than something tidier like 45 or 60 for the reason in the header:
 * chamfers and eased arrises put their neighbouring faces obtuse, so their
 * normals are far closer together than the corner looks, and a high threshold
 * silently deletes them. Low enough to keep those, high enough that a faceted
 * tube reads as round.
 */
const DEFAULT_ANGLE = 30;

export interface CreaseResult {
  /** One LineSegments carrying every part's creases, or null if none were found. */
  lines: THREE.LineSegments | null;
  /** How many segments it holds. */
  segments: number;
  /** How many parts contributed at least one crease. */
  parts: number;
  /** Wall-clock milliseconds spent building it — this is a CPU pass over every
   *  triangle in the model, so it is worth being able to quote. */
  ms: number;
}

/**
 * Build one merged crease-line object for every part given.
 *
 * Run per PART rather than over one merged geometry on purpose: merged, two
 * components that happen to touch would share an edge, and `EdgesGeometry` would
 * decide the junction between a bearing and the pier it sits on was a smooth
 * surface and drop the line — which is the same "one bit of identity" failure
 * that rules out a mask-based outline in the first place.
 */
export function buildCreases(
  parts: Array<{ positions: number[] }>,
  { angle = DEFAULT_ANGLE, colour = '#202020' }: { angle?: number; colour?: string } = {},
): CreaseResult {
  const t0 = performance.now();

  // Gathered then concatenated, rather than grown: the total is not known until
  // every part has been walked, and a growing array of a few million floats
  // reallocates its way through several times the final footprint.
  const chunks: Float32Array[] = [];
  let total = 0;
  let contributing = 0;

  for (const part of parts) {
    if (!part.positions || part.positions.length < 9) continue;

    // A throwaway geometry per part: EdgesGeometry takes a BufferGeometry, not a
    // raw array. Both are disposed immediately — neither is ever uploaded to the
    // GPU, so this costs CPU and heap only.
    const src = new THREE.BufferGeometry();
    src.setAttribute('position', new THREE.BufferAttribute(new Float32Array(part.positions), 3));

    const edges = new THREE.EdgesGeometry(src, angle);
    const attr = edges.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (attr && attr.count > 0) {
      const arr = attr.array as Float32Array;
      // Copied rather than referenced: `edges` is disposed on the next line and
      // its buffer must not be the thing we hand to the GPU later.
      chunks.push(new Float32Array(arr));
      total += arr.length;
      contributing++;
    }
    edges.dispose();
    src.dispose();
  }

  if (total === 0) {
    return { lines: null, segments: 0, parts: 0, ms: performance.now() - t0 };
  }

  const positions = new Float32Array(total);
  let at = 0;
  for (const c of chunks) {
    positions.set(c, at);
    at += c.length;
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));

  const mat = new THREE.LineBasicMaterial({ color: new THREE.Color(colour) });

  const lines = new THREE.LineSegments(geom, mat);
  lines.name = 'OutlineCreases';
  lines.castShadow = false;
  lines.receiveShadow = false;
  // World-space already, so the object stays at the origin — same as the pick
  // mesh and the merged engine-edge lines.
  lines.matrixAutoUpdate = false;
  lines.updateMatrixWorld(true);

  return {
    lines,
    segments: total / 6, // two endpoints × three floats per segment
    parts: contributing,
    ms: performance.now() - t0,
  };
}

/** Free a crease object's geometry and material. */
export function disposeCreases(lines: THREE.LineSegments | null): void {
  if (!lines) return;
  lines.geometry.dispose();
  (lines.material as THREE.Material).dispose();
}
